import {
  ApolloClient,
  CombinedGraphQLErrors,
  from,
  HttpLink,
  Observable,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import type { ArcTokenContextType } from "src/features/arc/ArcTokenProvider/context";
import { createCache } from "./createCache";

const ARC_GRAPHQL_ENDPOINT = process.env.NEXT_PUBLIC_ARC_GRAPHQL_ENDPOINT;

type CreateArcApolloClientOptions = ArcTokenContextType & {
  onIpRestricted: () => void;
};

export function createArcApolloClient(
  options: CreateArcApolloClientOptions,
): ApolloClient {
  const httpLink = new HttpLink({
    uri: ARC_GRAPHQL_ENDPOINT,
    credentials: "omit",
  });

  const authLink = setContext(async (_, { headers }) => {
    const result = await options.ensureToken();
    if (result.status !== "ok") throw new Error("Failed to obtain Arc token");
    return {
      headers: {
        ...headers,
        Authorization: `Bearer ${result.token}`,
      },
    };
  });

  const tokenRefreshLink = onError(({ error, operation, forward }) => {
    if (!CombinedGraphQLErrors.is(error)) return;

    const isIpRestricted = error.errors.some(
      (err) => err.extensions?.code === "IP_RESTRICTED",
    );
    if (isIpRestricted) {
      options.onIpRestricted();
      return;
    }

    const hasTokenExpired = error.errors.some(
      (err) => err.extensions?.code === "TOKEN_EXPIRED",
    );
    if (!hasTokenExpired) return;

    return new Observable((observer) => {
      options
        .refreshToken()
        .then((result) => {
          if (result.status !== "ok") {
            observer.error(new Error("Failed to refresh token"));
            return;
          }
          const oldHeaders = operation.getContext().headers;
          operation.setContext({
            headers: {
              ...oldHeaders,
              Authorization: `Bearer ${result.token}`,
            },
          });
          forward(operation).subscribe(observer);
        })
        .catch((err) => observer.error(err));
    });
  });

  return new ApolloClient({
    ssrMode: typeof window === "undefined",
    cache: createCache(),
    link: from([tokenRefreshLink, authLink, httpLink]),
    defaultOptions: {
      watchQuery: {
        notifyOnNetworkStatusChange: false,
      },
    },
  });
}
